import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";

export default function Staff() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "", role: "staff" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/staff");
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    try {
      await api.post("/staff", form);
      toast.success("Staff added. Share their login details.");
      setShowNew(false);
      setForm({ first_name: "", last_name: "", email: "", password: "", role: "staff" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not add"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this team member?")) return;
    try { await api.delete(`/staff/${id}`); toast.success("Removed"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not remove"); }
  };

  const isOwner = user?.role === "owner";

  return (
    <div className="max-w-5xl mx-auto px-6 py-8" data-testid="staff-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl tracking-tight font-bold">Team</h1>
          <div className="text-sm text-muted-foreground mt-1">{items.length} member{items.length === 1 ? "" : "s"}</div>
        </div>
        {isOwner && (
          <button onClick={() => setShowNew(true)} className="inline-flex items-center gap-2 rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90" data-testid="add-staff-button">
            <UserPlus className="h-4 w-4" /> Invite staff
          </button>
        )}
      </div>

      <div className="mt-6 rounded-xl border border-border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-accent text-left text-xs uppercase tracking-[0.14em] font-bold text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">Loading…</td></tr> :
             items.map((s) => (
              <tr key={s.id}>
                <td className="px-4 py-3 font-medium">{s.first_name} {s.last_name}</td>
                <td className="px-4 py-3 text-muted-foreground">{s.email}</td>
                <td className="px-4 py-3 capitalize">{s.role}</td>
                <td className="px-4 py-3 text-right">
                  {isOwner && s.role !== "owner" && (
                    <button onClick={() => remove(s.id)} className="text-rose-600 p-1.5 hover:bg-rose-50 rounded" data-testid={`remove-staff-${s.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </td>
              </tr>
             ))}
          </tbody>
        </table>
      </div>

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Invite team member</DialogTitle></DialogHeader>
          <form onSubmit={invite} className="space-y-3" data-testid="staff-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">First name</label>
                <input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="mt-1 w-full rounded-md border border-input px-3 py-2 text-sm bg-transparent" data-testid="staff-first-name" />
              </div>
              <div>
                <label className="text-sm font-medium">Last name</label>
                <input required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="mt-1 w-full rounded-md border border-input px-3 py-2 text-sm bg-transparent" data-testid="staff-last-name" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Email</label>
              <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 w-full rounded-md border border-input px-3 py-2 text-sm bg-transparent" data-testid="staff-email" />
            </div>
            <div>
              <label className="text-sm font-medium">Password</label>
              <input type="text" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 w-full rounded-md border border-input px-3 py-2 text-sm bg-transparent" data-testid="staff-password" />
              <div className="text-xs text-muted-foreground mt-1">Share this password with your staff. They can change it later.</div>
            </div>
            <div>
              <label className="text-sm font-medium">Role</label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-1 w-full rounded-md border border-input px-3 py-2 text-sm bg-transparent" data-testid="staff-role">
                <option value="staff">Staff</option>
                <option value="owner">Owner</option>
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowNew(false)} className="rounded-md border border-border px-4 py-2 text-sm">Cancel</button>
              <button type="submit" className="rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium" data-testid="staff-submit">Add</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
